'use client'

import {
    Campaign,
    CampaignStats,
    getCampaign,
    getCampaignStats,
    getCampaignLinkUrl,
    downloadCampaignLinkQrCode,
} from '@/lib/api/marketing/campaign-api'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdAttachMoney,
    MdBarChart,
    MdContentCopy,
    MdEdit,
    MdPeople,
    MdQrCode2,
    MdTouchApp,
    MdTrendingUp,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_BADGE: Record<string, string> = {
    DRAFT: 'badge-ghost',
    ACTIVE: 'badge-success',
    PAUSED: 'badge-warning',
    FINISHED: 'badge-info',
}

const STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativa',
    PAUSED: 'Pausada',
    FINISHED: 'Finalizada',
}

const TYPE_LABEL: Record<string, string> = {
    LEAD: 'Lead',
    APPOINTMENT: 'Agendamento',
    BILLING_PAID: 'Pagamento',
    CLIENT_SIGNUP: 'Cadastro',
}

function formatCurrency(value: number): string {
    return (value / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

export default function CampaignDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [campaign, setCampaign] = useState<Campaign | null>(null)
    const [stats, setStats] = useState<CampaignStats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (id) {
            loadData()
        }
    }, [id])

    const loadData = async () => {
        if (!id) return

        setLoading(true)
        try {
            const [campaignRes, statsRes] = await Promise.all([
                getCampaign(parseInt(id, 10)),
                getCampaignStats(parseInt(id, 10)),
            ])

            if (campaignRes.success && campaignRes.result) {
                setCampaign(campaignRes.result)
            } else {
                toast.error(campaignRes.error || 'Erro ao carregar campanha')
                navigate('/marketing/campaigns')
                return
            }

            if (statsRes.success && statsRes.result) {
                setStats(statsRes.result)
            }
        } catch {
            toast.error('Erro ao carregar dados da campanha')
            navigate('/marketing/campaigns')
        } finally {
            setLoading(false)
        }
    }

    const handleDownloadQrCode = async (link: NonNullable<Campaign['links']>[number]) => {
        try {
            await downloadCampaignLinkQrCode(campaign!.id, link)
        } catch {
            toast.error('Erro ao baixar o QR code')
        }
    }

    const copyToClipboard = (url: string) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.success('Link copiado para a área de transferência')
        }).catch(() => {
            toast.error('Erro ao copiar link')
        })
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    if (!campaign) {
        return null
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/marketing/campaigns')}
                    >
                        <MdArrowBack size={20} />
                    </button>
                    <h1 className="text-2xl font-bold">{campaign.name}</h1>
                    <span className={`badge ${STATUS_BADGE[campaign.status] || 'badge-ghost'}`}>
                        {STATUS_LABEL[campaign.status] || campaign.status}
                    </span>
                </div>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(`/marketing/campaigns/${campaign.id}/edit`)}
                >
                    <MdEdit size={18} />
                    Editar
                </button>
            </div>

            {/* Stat Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="stat bg-base-100 rounded-box shadow">
                        <div className="stat-figure text-blue-500">
                            <MdTouchApp size={28} />
                        </div>
                        <div className="stat-title">Total de Cliques</div>
                        <div className="stat-value text-blue-500">
                            {stats.totalClicks.toLocaleString('pt-BR')}
                        </div>
                    </div>

                    <div className="stat bg-base-100 rounded-box shadow">
                        <div className="stat-figure text-green-500">
                            <MdPeople size={28} />
                        </div>
                        <div className="stat-title">Total de Conversões</div>
                        <div className="stat-value text-green-500">
                            {stats.totalConversions.toLocaleString('pt-BR')}
                        </div>
                    </div>

                    <div className="stat bg-base-100 rounded-box shadow">
                        <div className="stat-figure text-purple-500">
                            <MdTrendingUp size={28} />
                        </div>
                        <div className="stat-title">Taxa de Conversão</div>
                        <div className="stat-value text-purple-500">
                            {stats.conversionRate}%
                        </div>
                    </div>

                    <div className="stat bg-base-100 rounded-box shadow">
                        <div className="stat-figure text-amber-500">
                            <MdAttachMoney size={28} />
                        </div>
                        <div className="stat-title">Receita Total</div>
                        <div className="stat-value text-amber-500 text-2xl">
                            {formatCurrency(stats.totalRevenue)}
                        </div>
                    </div>
                </div>
            )}

            {/* Desempenho por Origem */}
            {stats && stats.bySource.length > 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title flex items-center gap-2">
                            <MdBarChart size={22} />
                            Desempenho por Origem
                        </h2>
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Origem</th>
                                        <th>Meio</th>
                                        <th>Cliques</th>
                                        <th>Conversões</th>
                                        <th>Taxa</th>
                                        <th>Receita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.bySource.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="font-medium">{row.source}</td>
                                            <td>{row.medium || '-'}</td>
                                            <td>{row.clicks.toLocaleString('pt-BR')}</td>
                                            <td>{row.conversions.toLocaleString('pt-BR')}</td>
                                            <td>{row.conversionRate}%</td>
                                            <td>{formatCurrency(row.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Conversões por Tipo */}
            {stats && stats.byType.length > 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title">Conversões por Tipo</h2>
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Quantidade</th>
                                        <th>Receita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.byType.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="font-medium">
                                                {TYPE_LABEL[row.type] || row.type}
                                            </td>
                                            <td>{row.count.toLocaleString('pt-BR')}</td>
                                            <td>{formatCurrency(row.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Links da Campanha */}
            {campaign.links && campaign.links.length > 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title">Links da Campanha</h2>
                        <div className="space-y-3">
                            {campaign.links.map((link) => {
                                const displayUrl = getCampaignLinkUrl(link)

                                return (
                                    <div
                                        key={link.id}
                                        className="flex items-center justify-between gap-4 p-3 bg-base-200 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="badge badge-secondary badge-sm whitespace-nowrap">
                                                {link.source}
                                            </span>
                                            <span className="text-sm truncate">{displayUrl}</span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {link.clickCount !== undefined && (
                                                <span className="text-xs text-base-content/60">
                                                    <MdTouchApp className="inline mr-1" size={14} />
                                                    {link.clickCount.toLocaleString('pt-BR')}
                                                </span>
                                            )}
                                            <button
                                                className="btn btn-ghost btn-xs"
                                                onClick={() => copyToClipboard(displayUrl)}
                                                title="Copiar link"
                                            >
                                                <MdContentCopy size={16} />
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-xs"
                                                onClick={() => handleDownloadQrCode(link)}
                                                title="Baixar QR code"
                                            >
                                                <MdQrCode2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
